import { type } from 'arktype'

import type { ParameterlessTranslationKey } from '@orus.eu/translations'

import type { AbstractDimension, StateManagerCondition } from '../dimension/abstract-dimension.ts'

import type { SubscriptionBreadcrumbsPath } from './subscription-breadcrumbs.ts'
import type { SubscriptionFooterUiElement } from './subscription-footer-ui-element.ts'
import type { SubscriptionUiElement, SubscriptionUiElementGroup } from './subscription-ui-element.ts'

export const allSubscriptionStepIds = [
  'activity-search-main',
  'activity-category-search',
  'complementary-activities-selection',
  'es-rcph-activities-distribution',
  'general-information',
  'es-rcph-complementary-activities-selection',
  'es-mrpw-complementary-activities-selection',
  'es-mrpaxe-complementary-activities-selection',
  'es-rcph-prequote-form',
  'es-rcph-heavy-single-customer',
  'es-rcph-xp',
  'es-rcph-installs-security-material',
  'muta-personal-data',
  'muta-beneficiary-question',
  'muta-beneficiaries',
  'rcda-company-data',
  'rcda-experience',
  'rcda-previously-insured',
  'revenue',
  'es-mrp-company-info',
  'es-mrpaxe-company-info',
  'mrp-occupation-status',
  'es-mrp-premises-info',
  'es-mrp-assets-value',
  'es-mrpaxe-premises-info',
  'es-mrpaxe-assets-value',
  'mrp-assets-value',
  'mrp-revenue',
  'mrpw-claims',
  'fr-mrpaxe-claims',
  'mrph-rc-claims',
  'mrph-claims',
  'mrph-it-assets-value',
  'mrph-formula',
  'mrp-siret-search',
  'mrp-confirm-address',
  'mrp-add-address',
  'rcph-cyber-information',
  'mrpw-pizzeria-mgt-xp',
  'fr-mrpaxe-pizzeria-mgt-xp',
  'rcpa-prequote-form',
  'rcpa-persona-question',
  'rcpaxa-prequote-form',
  'rcpaxa-activities-distribution',
  'rcpa-subsidiaries-question',
  'rcpa-add-subsidiaries',
  'rcpa-finalize-subsidiaries',
  'rcpa-cif-workforce',
  'rcpa-agents-question',
  'rcpa-add-agents',
  'estimated-start-date',
  'personal-data',
  'quote',
  'coverage',
  'post-quote-rcda-revenue-data',
  'post-quote-risk-assessment',
  'post-quote-mrpw-risk-assessment',
  'post-quote-fr-mrpaxe-risk-assessment',
  'post-quote-es-mrpaxe-risk-assessment',
  'statement',
  'post-quote-siret-search',
  'post-quote-kvk-search',
  'post-quote-confirm-address',
  'post-quote-add-address',
  'additional-personal-data',
  'bank-account',
  'confirm-compliance-locking',
  'confirm-documents-locking',
  'contract-change-recap',
  'endorsement-validation-request',
  'waiting-for-validation',
  'locked',
  'checkout',
  'activity-specific-declaration',
  'mrpa-local-questions',
  'mrpa-company-questions',
  'mrpa-claims',
  'mrpa-post-quote-specific-questions',
  'mrpa-activities-distribution',
  'fr-prel-personal-data',
  'fr-prel-professional-data',
  'fr-prel-health-simplified',
  'fr-prel-health-history',
  'fr-prel-health-questions',
  'fr-prel-fiscality',
  'nl-rcph-business-information',
  'nl-rcph-subsidiaries',
  'nl-aovql-personal-information',
  'fr-prel-statement',
  'nl-aovql-forms-gate',
  'nl-aovql-statement',
  'fr-rcp-general-info',
  'fr-rcp-activities-distribution',
  'fr-rccw-general-info',
  'fr-rccw-vehicle-registration-search',
  'fr-rccw-vehicle',
  'fr-rccw-main-driver',
  'fr-rccw-declarations',
  'fr-aupa-general-info',
  'fr-mrga-company-info',
  'fr-mrga-premises-info',
  'contact-data',
  'fr-aupa-vehicle-registration-search',
  'fr-aupa-vehicle',
  'fr-aupa-main-driver',
  'fr-aupa-declarations',
  'fr-aupa-additional-driver-data',
] as const
export const subscriptionStepIdSchema = type('===', ...allSubscriptionStepIds)
export type SubscriptionStepId = typeof subscriptionStepIdSchema.infer

const subscriptionDetailTypes = ['guarantees'] as const

type SubscriptionDetailType = (typeof subscriptionDetailTypes)[number]

export function isSubscriptionDetailType(value: string): value is SubscriptionDetailType {
  return (subscriptionDetailTypes as readonly string[]).includes(value)
}

export type SubscriptionDetail = {
  type: SubscriptionDetailType
  id: string
}

type SubscriptionUiStepTitleValue = { type: 'text'; value: string } | { type: 'key'; key: ParameterlessTranslationKey }

export type SubscriptionUiStepTitle = {
  /** Title when in quote context or when no override is applied */
  quoteAndDefault: SubscriptionUiStepTitleValue | null
  contextOverride?: {
    /** Title when in endorsement context */
    endorsement?: SubscriptionUiStepTitleValue | null
  }
}

export type SubscriptionUiStep = {
  id: SubscriptionStepId
  /**
   * A list of breadcrums of length 1 or 2 (UI design only supports this)
   */
  breadcrumbs: SubscriptionBreadcrumbsPath
  details?: { [key in SubscriptionDetailType]?: SubscriptionUiElementGroup }
  condition?: StateManagerCondition
  title: SubscriptionUiStepTitle
  hideForExistingCustomers?: boolean
  showProgress: boolean
  subtitleElement?: SubscriptionUiElement
  bodyElements: SubscriptionUiElementGroup
  footerElement?: SubscriptionFooterUiElement
  /**
   * A list of side elements.
   * No column will be added if set to undefined.
   * An empty column will be added if set to an empty array
   */
  sideElements?: SubscriptionUiElementGroup
  sideElementsMobileVariant?: 'default' | 'card' | 'hidden'
  /**
   * Optionally customize the next step label (default is "continue")
   */
  nextStepLabelKey?: ParameterlessTranslationKey
  /**
   * Optionnally show a floating go to bottom button
   */
  jumpToBottom?: boolean
  /**
   * Optionally add a tip before the next step label (default is "Continuer")
   */
  nextStepLabelTip?: string
  /**
   * Optionally add an ui element before the next step label
   */
  secondaryNavigationUiElement?: SubscriptionUiElement
  /**
   * These dimensions will not block completion of the step
   */
  partiallyUsedInputDimensions?: AbstractDimension[]
  /**
   * Optionally hide the footer element
   */
  hideFooterElement?: boolean
  /**
   * Optionally hide the next step button, the previous step button stays
   */
  hideNextStepButton?: boolean
}
